import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { formatTemp } from '../utils/temperature';

const METRICS = [
  { id: 'bp', label: 'Blood pressure' },
  { id: 'weight', label: 'Weight (kg)' },
  { id: 'pulse', label: 'Pulse (bpm)' },
  { id: 'temp', label: 'Temperature' },
  { id: 'rr', label: 'Resp. rate' },
];

function formatTick(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function tooltipLabel(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

function formatSource(source) {
  if (!source) return '—';
  const labels = {
    intake_update: 'Intake save',
    staff_recorded: 'Staff recorded',
    visit_approved: 'Visit approved',
  };
  return labels[source] || String(source).replace(/_/g, ' ');
}

function cellDash(v) {
  if (v === null || v === undefined || v === '') return '—';
  return v;
}

export default function PatientVitalsChart({ readings }) {
  const [metric, setMetric] = useState('bp');
  const [selectedId, setSelectedId] = useState(null);

  const sortedReadings = useMemo(() => {
    if (!readings?.length) return [];
    return [...readings].sort((a, b) => new Date(b.recorded_at) - new Date(a.recorded_at));
  }, [readings]);

  useEffect(() => {
    if (!sortedReadings.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && sortedReadings.some((r) => r.id === prev)) return prev;
      return sortedReadings[0].id;
    });
  }, [sortedReadings]);

  const selectedReading = useMemo(
    () => sortedReadings.find((r) => r.id === selectedId) || null,
    [sortedReadings, selectedId]
  );

  const chartData = useMemo(() => {
    if (!readings?.length) return [];
    return readings.map((r) => ({
      t: r.recorded_at,
      bpSys: r.bp_systolic ?? null,
      bpDia: r.bp_diastolic ?? null,
      weight: r.weight_kg ?? null,
      pulse: r.pulse ?? null,
      temp: r.temperature ?? null,
      rr: r.respiratory_rate ?? null,
      source: r.source,
    }));
  }, [readings]);

  const hasAny = chartData.some(
    (row) =>
      row.bpSys != null ||
      row.bpDia != null ||
      row.weight != null ||
      row.pulse != null ||
      row.temp != null ||
      row.rr != null
  );

  if (!readings?.length) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No vitals history yet. Points are added when intake saves vitals, staff record vitals, or when a visit note is
          approved.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Multiple visits in a month will appear as separate points on the timeline.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        All readings (newest first). Click a row to see full vitals for that date and time.
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2, maxHeight: 280 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{'Date & time'}</TableCell>
              <TableCell align="right">BP</TableCell>
              <TableCell align="right">Wt (kg)</TableCell>
              <TableCell align="right">Ht (cm)</TableCell>
              <TableCell align="right">Temp</TableCell>
              <TableCell align="right">Pulse</TableCell>
              <TableCell align="right">RR</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedReadings.map((r) => (
              <TableRow
                key={r.id}
                hover
                selected={selectedId === r.id}
                onClick={() => setSelectedId(r.id)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  {r.recorded_at
                    ? new Date(r.recorded_at).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                    : '—'}
                </TableCell>
                <TableCell align="right">
                  {r.bp_systolic != null && r.bp_diastolic != null
                    ? `${r.bp_systolic}/${r.bp_diastolic}`
                    : '—'}
                </TableCell>
                <TableCell align="right">{cellDash(r.weight_kg)}</TableCell>
                <TableCell align="right">{cellDash(r.height_cm)}</TableCell>
                <TableCell align="right">{cellDash(r.temperature) === '—' ? '—' : formatTemp(r.temperature)}</TableCell>
                <TableCell align="right">{cellDash(r.pulse)}</TableCell>
                <TableCell align="right">{cellDash(r.respiratory_rate)}</TableCell>
                <TableCell>{formatSource(r.source)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedReading && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Vitals for selected time
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {selectedReading.recorded_at
              ? new Date(selectedReading.recorded_at).toLocaleString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '—'}
            {' · '}
            {formatSource(selectedReading.source)}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 1.5,
            }}
          >
            <Typography variant="body2">
              <strong>Blood pressure:</strong>{' '}
              {selectedReading.bp_systolic != null && selectedReading.bp_diastolic != null
                ? `${selectedReading.bp_systolic}/${selectedReading.bp_diastolic} mmHg`
                : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Weight:</strong>{' '}
              {selectedReading.weight_kg != null ? `${selectedReading.weight_kg} kg` : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Height:</strong>{' '}
              {selectedReading.height_cm != null ? `${selectedReading.height_cm} cm` : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Temperature:</strong>{' '}
              {selectedReading.temperature != null ? formatTemp(selectedReading.temperature) : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Pulse:</strong>{' '}
              {selectedReading.pulse != null ? `${selectedReading.pulse} bpm` : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Respiratory rate:</strong>{' '}
              {selectedReading.respiratory_rate != null ? `${selectedReading.respiratory_rate} rpm` : '—'}
            </Typography>
          </Box>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
          Chart metric
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={metric}
          onChange={(_, v) => v != null && setMetric(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          {METRICS.map((m) => (
            <ToggleButton key={m.id} value={m.id}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, bgcolor: 'background.default' }}>
        <Box sx={{ width: '100%', height: hasAny ? 320 : 'auto', minHeight: hasAny ? 320 : 0, py: hasAny ? 0 : 2, px: hasAny ? 0 : 1 }}>
          {hasAny ? (
            <ResponsiveContainer width="100%" height="100%">
              {metric === 'bp' ? (
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="t" tickFormatter={formatTick} minTickGap={24} fontSize={12} />
                  <YAxis fontSize={12} domain={['auto', 'auto']} />
                  <Tooltip
                    labelFormatter={tooltipLabel}
                    formatter={(value, name) => [value, name === 'bpSys' ? 'Systolic' : 'Diastolic']}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="bpSys" name="Systolic" stroke="#0d9488" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  <Line type="monotone" dataKey="bpDia" name="Diastolic" stroke="#0369a1" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                </LineChart>
              ) : (
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="t" tickFormatter={formatTick} minTickGap={24} fontSize={12} />
                  <YAxis fontSize={12} domain={['auto', 'auto']} />
                  <Tooltip labelFormatter={tooltipLabel} />
                  <Legend />
                  {metric === 'weight' && (
                    <Line type="monotone" dataKey="weight" name="Weight (kg)" stroke="#047857" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  )}
                  {metric === 'pulse' && (
                    <Line type="monotone" dataKey="pulse" name="Pulse" stroke="#b45309" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  )}
                  {metric === 'temp' && (
                    <Line type="monotone" dataKey="temp" name="Temperature" stroke="#be123c" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  )}
                  {metric === 'rr' && (
                    <Line type="monotone" dataKey="rr" name="RR" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  )}
                </LineChart>
              )}
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              No numeric values to plot yet. Use the table above to review each recorded time.
            </Typography>
          )}
        </Box>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%', mb: 0.5 }}>
          Sources
        </Typography>
        <Chip size="small" label="Intake save" variant="outlined" />
        <Chip size="small" label="Staff recorded" variant="outlined" />
        <Chip size="small" label="Visit approved" variant="outlined" />
      </Box>
    </Box>
  );
}
